import { useEffect, useState } from 'react'
import Layout from '@/components/Layout'
import { fetchLanguages, createLanguage, updateLanguage, deleteLanguage } from '@/lib/api'
import type { Language } from '@/lib/types'

export default function LanguagesList() {
  const [items, setItems] = useState<Language[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<string | null>(null)
  const [newName, setNewName] = useState('')
  const [showNew, setShowNew] = useState(false)

  useEffect(() => { fetchLanguages().then(l => { setItems(l); setLoading(false) }) }, [])
  async function handleUpdate(id: string) { const s = items.find(i => i.id === id); if (!s) return; await updateLanguage(id, { name: s.name }); setEditing(null) }
  async function handleCreate() { if (!newName) return; await createLanguage({ name: newName }); setShowNew(false); setNewName(''); fetchLanguages().then(setItems) }
  async function handleDelete(id: string, name: string) { if (!window.confirm(`Delete "${name}"?`)) return; await deleteLanguage(id); setItems(prev => prev.filter(i => i.id !== id)) }

  const action = (
    <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-all duration-200 shadow-lg shadow-brand-500/20 hover:shadow-brand-500/30">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
      Add Language
    </button>
  )

  return (
    <Layout title="Languages" action={action}>
      <div className="max-w-2xl mx-auto space-y-5">
        {showNew && (
          <div className="bg-white rounded-2xl border border-surface-200 shadow-card p-6">
            <h2 className="text-sm font-semibold text-slate-900 mb-4">New Language</h2>
            <div className="flex gap-4">
              <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Japanese" className="flex-1 px-3 py-2.5 rounded-xl border border-surface-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" />
              <button onClick={handleCreate} className="bg-brand-600 text-white text-sm font-semibold px-5 py-2.5 rounded-xl hover:bg-brand-700">Create</button>
              <button onClick={() => setShowNew(false)} className="text-sm font-medium text-slate-600 px-4 py-2.5 rounded-xl border border-surface-200 hover:bg-surface-50">Cancel</button>
            </div>
          </div>
        )}
        <div className="bg-white rounded-2xl border border-surface-200 shadow-card overflow-hidden">
          <table className="w-full">
            <thead><tr className="bg-surface-50/50"><th className="text-left px-5 py-3.5 font-semibold text-[11px] text-slate-500 uppercase tracking-wider">Language</th><th className="text-right px-5 py-3.5 font-semibold text-[11px] text-slate-500 uppercase tracking-wider">Actions</th></tr></thead>
            <tbody className="divide-y divide-surface-100">
              {loading ? [1,2,3].map(i => (<tr key={i} className="animate-pulse"><td className="px-5 py-4"><div className="h-4 w-32 bg-surface-100 rounded" /></td><td className="px-5 py-4"><div className="h-4 w-20 bg-surface-100 rounded ml-auto" /></td></tr>)) : items.map(item => (
                <tr key={item.id} className="hover:bg-surface-50/50 transition-colors">
                  <td className="px-5 py-4">
                    {editing === item.id ? <input value={item.name} onChange={e => setItems(prev => prev.map(x => x.id === item.id ? { ...x, name: e.target.value } : x))} className="w-full px-2 py-1.5 rounded-lg border border-surface-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20" /> : <span className="text-sm font-semibold text-slate-900">{item.name}</span>}
                  </td>
                  <td className="px-5 py-4 text-right">
                    {editing === item.id ? (
                      <div className="flex justify-end gap-1.5"><button onClick={() => handleUpdate(item.id)} className="text-[12px] font-semibold text-white bg-brand-600 hover:bg-brand-700 px-3 py-1.5 rounded-lg">Save</button><button onClick={() => setEditing(null)} className="text-[12px] font-medium text-slate-600 hover:bg-surface-100 px-3 py-1.5 rounded-lg">Cancel</button></div>
                    ) : (
                      <div className="flex justify-end gap-1"><button onClick={() => setEditing(item.id)} className="text-[12px] font-medium text-slate-500 hover:text-brand-600 bg-surface-100 hover:bg-brand-50 px-3 py-1.5 rounded-lg transition-colors">Edit</button><button onClick={() => handleDelete(item.id, item.name)} className="text-[12px] font-medium text-slate-500 hover:text-red-600 bg-surface-100 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors">Delete</button></div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  )
}
